// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SettingsTabContainer, SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {I18N_EMAIL, I18N_EMAIL_MAILTO, PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import * as EmojiUtils from '@app/features/expressions/utils/EmojiUtils';
import Spellcheck from '@app/features/messaging/state/Spellcheck';
import type {SpellcheckEngine} from '@app/features/platform/types/electron';
import {Button} from '@app/features/ui/button/Button';
import {Select, type SelectOption} from '@app/features/ui/components/form/Select';
import {Switch} from '@app/features/ui/components/form/Switch';
import type {RadioOption} from '@app/features/ui/radio_group/RadioGroup';
import {RadioGroup} from '@app/features/ui/radio_group/RadioGroup';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import * as NativeUtils from '@app/features/ui/utils/NativeUtils';
import * as UserSettingsCommands from '@app/features/user/commands/UserSettingsCommands';
import styles from '@app/features/user/components/modals/tabs/LanguageTab.module.css';
import UserSettings from '@app/features/user/state/UserSettings';
import * as LocaleUtils from '@app/features/user/utils/LocaleUtils';
import {TimeFormatTypes} from '@app/vendor/constants/UserConstants';
import {getFormattedTime} from '@app/vendor/date_utils/DateFormatting';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo, useState} from 'react';
import type {FilterOptionOption} from 'react-select';

interface LanguageSelectOption extends SelectOption<string> {
	code: string;
	name: string;
	nativeName: string;
	flag: string;
	searchText: string;
}
function normalizeLanguageSearchText(value: string): string {
	return value
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase();
}
const LanguageTab = observer(() => {
	const {t} = useLingui();
	const currentLocale = LocaleUtils.getCurrentLocale();
	const availableLocales = LocaleUtils.getSortedLocales();
	const {timeFormat} = UserSettings;
	const isDesktop = NativeUtils.isDesktop();
	const getAutoTimeFormatDescription = () => {
		const appLocale = UserSettings.getLocale();
		const browserLocale = navigator.language;
		const effectiveLocale = Accessibility.useBrowserLocaleForTimeFormat ? browserLocale : appLocale;
		const localeUses12Hour = (locale: string): boolean => {
			const lang = locale.toLowerCase();
			const twelveHourLocales = [
				'en-us',
				'en-ca',
				'en-au',
				'en-nz',
				'en-ph',
				'en-in',
				'en-pk',
				'en-bd',
				'en-za',
				'es-mx',
				'es-co',
				'ar',
				'hi',
				'bn',
				'ur',
				'fil',
				'tl',
			];
			return twelveHourLocales.some((l) => lang.startsWith(l));
		};
		const uses12Hour = localeUses12Hour(effectiveLocale);
		const sampleDate = new Date(2025, 0, 1, 14, 30, 0);
		const format = getFormattedTime(sampleDate, effectiveLocale, uses12Hour);
		if (Accessibility.useBrowserLocaleForTimeFormat) {
			return isDesktop
				? t`System locale (${browserLocale}): ${format}`
				: t`Browser locale (${browserLocale}): ${format}`;
		}
		return t`App language (${appLocale}): ${format}`;
	};
	const get12HourExample = () => {
		const locale = UserSettings.getLocale();
		const sampleDate = new Date(2025, 0, 1, 14, 30, 0);
		return getFormattedTime(sampleDate, locale, true);
	};
	const get24HourExample = () => {
		const locale = UserSettings.getLocale();
		const sampleDate = new Date(2025, 0, 1, 14, 30, 0);
		return getFormattedTime(sampleDate, locale, false);
	};
	const timeFormatOptions: ReadonlyArray<RadioOption<number>> = [
		{value: TimeFormatTypes.AUTO, name: t`Auto`, desc: getAutoTimeFormatDescription()},
		{value: TimeFormatTypes.TWELVE_HOUR, name: t`12-hour`, desc: get12HourExample()},
		{value: TimeFormatTypes.TWENTY_FOUR_HOUR, name: t`24-hour`, desc: get24HourExample()},
	];
	const handleLocaleChange = (newLocale: string) => {
		LocaleUtils.setLocale(newLocale);
	};
	const localeOptions = useMemo<ReadonlyArray<LanguageSelectOption>>(
		() =>
			availableLocales.map((locale) => ({
				value: locale.code,
				label: `${locale.nativeName} ${locale.name}`,
				code: locale.code,
				name: locale.name,
				nativeName: locale.nativeName,
				flag: locale.flag,
				searchText: normalizeLanguageSearchText(`${locale.nativeName} ${locale.name} ${locale.code}`),
			})),
		[availableLocales],
	);
	const renderLanguageContent = useCallback((option: LanguageSelectOption, selected: boolean, compact = false) => {
		const isEnGB = option.code === 'en-GB';
		const flagImg = (
			<img
				src={EmojiUtils.getEmojiURL(option.flag) ?? undefined}
				alt={`${option.name} flag`}
				className={styles.flagImage}
				draggable={false}
			/>
		);
		return (
			<div
				className={clsx(
					styles.languageOption,
					selected && styles.languageOptionSelected,
					compact && styles.languageOptionCompact,
				)}
			>
				<span className={styles.languageName}>{option.nativeName}</span>
				<div className={styles.languageDetails}>
					<span className={styles.languageCode}>{option.name}</span>
					{isEnGB ? (
						<Tooltip
							text={() => (
								<span className={styles.tooltipContent}>
									<span className={styles.tooltipText}>
										<Trans>For British eyes only...</Trans>
									</span>
								</span>
							)}
						>
							{flagImg}
						</Tooltip>
					) : (
						flagImg
					)}
				</div>
			</div>
		);
	}, []);
	const filterLanguageOption = useCallback((option: FilterOptionOption<LanguageSelectOption>, inputValue: string) => {
		const query = normalizeLanguageSearchText(inputValue.trim());
		return query.length === 0 || option.data.searchText.includes(query);
	}, []);
	return (
		<SettingsTabContainer>
			<SettingsTabSection
				title={<Trans>Time Format</Trans>}
				description={<Trans>Choose how times are displayed throughout the app.</Trans>}
			>
				<RadioGroup
					options={timeFormatOptions}
					value={timeFormat}
					onChange={(value) => UserSettingsCommands.update({timeFormat: value})}
					aria-label={t`Time format selection`}
				/>
				{timeFormat === TimeFormatTypes.AUTO && (
					<div className={styles.switchWrapper}>
						<Switch
							label={isDesktop ? t`Use system locale for time format` : t`Use browser locale for time format`}
							description={
								isDesktop
									? t`When enabled, uses your computer's locale to determine 12/24-hour format instead of the app's language setting.`
									: t`When enabled, uses your browser's locale to determine 12/24-hour format instead of the app's language setting.`
							}
							value={Accessibility.useBrowserLocaleForTimeFormat}
							onChange={(value) => AccessibilityCommands.update({useBrowserLocaleForTimeFormat: value})}
						/>
					</div>
				)}
			</SettingsTabSection>
			<div className={styles.notice}>
				<p className={styles.noticeText}>
					<Trans>
						All translations are currently LLM-generated with minimal human revision. We'd love to get real people to
						help us localize Fluxer into your language! To do so, send an email to{' '}
						<a href={I18N_EMAIL_MAILTO} className={styles.link}>
							{I18N_EMAIL}
						</a>{' '}
						and we'll be happy to accept your contributions.
					</Trans>
				</p>
			</div>
			<SettingsTabSection
				title={<Trans>Language Settings</Trans>}
				description={<Trans>Choose your preferred language for the interface.</Trans>}
			>
				<Select<string, false, LanguageSelectOption>
					options={localeOptions}
					value={currentLocale}
					onChange={handleLocaleChange}
					renderOption={(option, selected) => renderLanguageContent(option, selected)}
					renderValue={(option) => (option ? renderLanguageContent(option, false, true) : null)}
					filterOption={filterLanguageOption}
					placeholder={t`Search languages...`}
					isSearchable={true}
					openMenuOnFocus={true}
					maxMenuHeight={280}
					className={styles.languageSelect}
					aria-label={t`Select interface language`}
				/>
			</SettingsTabSection>
			<SpellcheckSettingsSection />
		</SettingsTabContainer>
	);
});
const SpellcheckSettingsSection = observer(() => {
	const {t} = useLingui();
	const isDesktop = NativeUtils.isDesktop();
	const [newWord, setNewWord] = useState('');
	if (!isDesktop) {
		return (
			<SettingsTabSection
				title={<Trans>Spellcheck</Trans>}
				description={
					<Trans>
						Spellcheck on the web uses your browser's built-in checker. Install the desktop app for the in-app Hunspell
						engine, custom personal dictionary, and per-language overrides.
					</Trans>
				}
			>
				<Switch
					label={t`Enable spellcheck`}
					value={Spellcheck.enabled}
					onChange={(value) => Spellcheck.setEnabled(value)}
				/>
			</SettingsTabSection>
		);
	}
	const engineOptions: ReadonlyArray<RadioOption<SpellcheckEngine>> = [
		{
			value: 'auto',
			name: t`Auto`,
			desc: t`Use the in-app Hunspell engine when a ${PRODUCT_NAME} dictionary matches the selected language; otherwise fall back to your operating system's spellchecker.`,
		},
		{
			value: 'hunspell',
			name: t`In-app (Hunspell)`,
			desc: t`Always use ${PRODUCT_NAME}'s Hunspell dictionaries, downloaded on demand. Ignores macOS's smart corrections and Windows's spellchecker.`,
		},
		{
			value: 'system',
			name: t`Operating system`,
			desc: t`Use the OS-level spellchecker (NSSpellChecker on macOS, Windows Spellcheck on Windows, Hunspell+downloaded dictionaries on Linux).`,
		},
	];
	const isHunspellActive =
		Spellcheck.resolvedEngine?.mode === 'hunspell' ||
		(Spellcheck.resolvedEngine === null && (Spellcheck.engine === 'auto' || Spellcheck.engine === 'hunspell'));
	const selectedTagSet = new Set(Spellcheck.languages.map((l) => l.toLowerCase()));
	const handleAddWord = () => {
		const trimmed = newWord.trim();
		if (!trimmed) return;
		Spellcheck.addPersonalWord(trimmed);
		setNewWord('');
	};
	return (
		<SettingsTabSection
			title={<Trans>Spellcheck</Trans>}
			description={
				<Trans>
					Underline misspelled words while you type. Fluxer uses its own Hunspell dictionaries, downloaded on demand, so
					spellcheck works the same on Windows, macOS, and Linux — independent of your operating system.
				</Trans>
			}
		>
			<Switch
				label={t`Enable spellcheck`}
				value={Spellcheck.enabled}
				onChange={(value) => Spellcheck.setEnabled(value)}
			/>
			{Spellcheck.enabled && (
				<>
					<div className={styles.spellcheckSubsection}>
						<RadioGroup
							options={engineOptions}
							value={Spellcheck.engine}
							onChange={(value) => Spellcheck.setEngine(value)}
							aria-label={t`Spellcheck engine`}
						/>
					</div>
					{Spellcheck.reloadRequired && (
						<div className={styles.spellcheckRestartBanner}>
							<Trans>
								Reload Fluxer to fully apply the engine change. (Switching between in-app and system spellcheck requires
								a renderer reload — Electron can't swap providers in-flight.)
							</Trans>{' '}
							<Button
								small
								variant="secondary"
								onClick={() => {
									if (typeof window !== 'undefined') window.location.reload();
								}}
							>
								<Trans>Reload now</Trans>
							</Button>
						</div>
					)}
					<div className={styles.spellcheckSubsection}>
						<Switch
							label={t`Auto-detect language`}
							description={t`Pick the active dictionary from your interface language and operating system locale. Turn off to choose dictionaries manually below.`}
							value={Spellcheck.autoDetect}
							onChange={(value) => Spellcheck.setAutoDetect(value)}
						/>
					</div>
					{!Spellcheck.autoDetect && (
						<div className={styles.spellcheckSubsection}>
							<div className={styles.spellcheckHint}>
								<Trans>
									Pick one or more Hunspell dictionaries. Fluxer downloads them when needed. Enabling multiple lets
									Fluxer accept words from any of them — useful for multilingual chats.
								</Trans>
							</div>
							<div className={styles.dictionaryGrid}>
								{Spellcheck.bundledDictionaries.map((dict) => {
									const checked = selectedTagSet.has(dict.tag.toLowerCase());
									return (
										<label
											key={`${dict.package}-${dict.tag}`}
											className={clsx(styles.dictionaryRow, checked && styles.dictionaryRowChecked)}
										>
											<input type="checkbox" checked={checked} onChange={() => Spellcheck.toggleLanguage(dict.tag)} />
											<span className={styles.dictionaryName}>{dict.nativeName}</span>
											<span className={styles.dictionaryTag}>{dict.tag}</span>
										</label>
									);
								})}
							</div>
							{Spellcheck.engine === 'system' && (
								<div className={styles.spellcheckHint}>
									<Trans>
										In "Operating system" mode, dictionaries listed above are advisory — {PRODUCT_NAME} will pass these
										language tags to your OS spellchecker when supported. Tags it doesn't recognize are ignored.
									</Trans>
								</div>
							)}
						</div>
					)}
					{isHunspellActive && (
						<div className={styles.personalDictWrapper}>
							<div className={styles.spellcheckHint}>
								<Trans>
									Personal dictionary — words you've added are accepted across every active dictionary and synced across
									your devices. The word "{PRODUCT_NAME}" is built in.
								</Trans>
							</div>
							<div className={styles.personalDictAddRow}>
								<input
									type="text"
									className={styles.personalDictInput}
									placeholder={t`Add a word…`}
									value={newWord}
									onChange={(event) => setNewWord(event.target.value)}
									onKeyDown={(event) => {
										if (event.key === 'Enter') {
											event.preventDefault();
											handleAddWord();
										}
									}}
								/>
								<Button small variant="secondary" onClick={handleAddWord} disabled={!newWord.trim()}>
									<Trans>Add</Trans>
								</Button>
							</div>
							{Spellcheck.personalDictionary.length === 0 ? (
								<div className={styles.personalDictEmpty}>
									<Trans>No personal words yet.</Trans>
								</div>
							) : (
								<div className={styles.personalDictList}>
									{Spellcheck.personalDictionary.map((word) => (
										<span key={word} className={styles.personalDictChip}>
											{word}
											<button
												type="button"
												className={styles.personalDictChipRemove}
												aria-label={t`Remove "${word}" from dictionary`}
												onClick={() => Spellcheck.removePersonalWord(word)}
											>
												×
											</button>
										</span>
									))}
								</div>
							)}
						</div>
					)}
				</>
			)}
		</SettingsTabSection>
	);
});

export default LanguageTab;
